import TreeSet from 'ml-tree-set';
import { getConnectivityMatrix } from 'openchemlib-utils';
import { predictCarbon } from '../../../prediction/predictCarbon';
import { predictProton } from '../../../prediction/predictProton';
import { createMapPossibleAssignment, } from './createMapPossibleAssignment';
import { exploreTree } from './exploreTree';
import { isSpectraData1D } from './isSpectraData1D';
import { searchIndices } from './searchIndices';
const comparator = (a, b) => {
    return b.score - a.score;
};
const predictor = { H: predictProton, C: predictCarbon };
function checkNMRSignal1D(signals) {
    const keys = [
        'nbAtoms',
        'diaIDs',
        'atoms',
    ];
    for (const signal of signals) {
        for (let key of keys) {
            if (!signal[key])
                throw new Error(`property ${key} does not exist`);
        }
    }
}
export const getAllHydrogens = {
    C: (m, i) => m.getAllHydrogens(i),
    H: () => 1,
};
export async function buildAssignments(props) {
    const { spectra, molecule, restrictionByCS, timeout, minScore, nbAllowedUnAssigned = {}, maxSolutions, correlations, assignmentOrder, predictionOptions, predictions: inputPrediction = {}, targets, } = props;
    let date = new Date();
    let timeStart = date.getTime();
    let lowerBoundScore = minScore;
    let store = {
        solutions: new TreeSet(comparator),
        nSolutions: 0,
    };
    const pathLengthMatrix = getConnectivityMatrix(molecule, {
        pathLength: true,
    });
    let infoByAtomType = {};
    const predictions = {};
    let possibleAssignmentMap = {};
    let diaIDPeerPossibleAssignment = {};
    for (const atomTypesToPredict of assignmentOrder) {
        for (const atomType of atomTypesToPredict) {
            const options = predictionOptions[atomType];
            const predictedSignals = inputPrediction[atomType];
            let { joinedSignals } = predictedSignals
                ? { joinedSignals: predictedSignals }
                : await predictor[atomType](molecule, options);
            checkNMRSignal1D(joinedSignals);
            if (!predictions[atomType])
                predictions[atomType] = {};
            let totalHydrogens = 0;
            for (let prediction of joinedSignals) {
                const diaID = prediction.diaIDs[0];
                const index = prediction.atoms[0];
                const allHydrogens = getAllHydrogens[atomType](molecule, index);
                totalHydrogens += prediction.nbAtoms * allHydrogens;
                predictions[atomType][diaID] = {
                    ...prediction,
                    diaIDIndex: index,
                    allHydrogens: prediction.nbAtoms * allHydrogens,
                    protonsCount: allHydrogens,
                    pathLength: pathLengthMatrix[index],
                };
            }
            for (let diaID in predictions[atomType]) {
                predictions[atomType][diaID].allHydrogens *= 100 / totalHydrogens;
            }
            infoByAtomType[atomType] = {
                nSources: joinedSignals.length,
                currentIndex: 0,
                nbAllowedUnAssigned: nbAllowedUnAssigned[atomType] || 0,
            };
        }
        possibleAssignmentMap = createMapPossibleAssignment(possibleAssignmentMap, {
            restrictionByCS,
            predictions,
            targets,
        });
        diaIDPeerPossibleAssignment = {};
        for (const atomType in possibleAssignmentMap) {
            diaIDPeerPossibleAssignment[atomType] = Object.keys(possibleAssignmentMap[atomType]);
        }
        let sourceOfPartials = getSourceOfPartials(store, infoByAtomType, atomTypesToPredict);
        store = {
            solutions: new TreeSet(comparator),
            nSolutions: 0,
        };
        for (let partial of sourceOfPartials) {
            exploreTree({
                currentAtomTypes: atomTypesToPredict,
                restrictionByCS,
                timeout,
                timeStart,
                targets,
                predictions,
                correlations,
                maxSolutions,
                lowerBoundScore,
                possibleAssignmentMap,
                diaIDPeerPossibleAssignment,
            }, infoByAtomType, partial, store);
        }
    }
    return annotateSpectraData({
        store,
        spectra,
        diaIDPeerPossibleAssignment,
        targets,
    });
}
function annotateSpectraData(input) {
    var _a, _b;
    const { store, spectra, diaIDPeerPossibleAssignment, targets } = input;
    const { solutions } = store;
    const mapSignalId = {};
    const atomTypes = Object.keys(targets);
    for (const atomType of atomTypes) {
        const targetByAtomType = targets[atomType];
        for (const targetId in targetByAtomType) {
            let target = targetByAtomType[targetId];
            for (const link of target.link) {
                const signalId = link.signal.id;
                if (mapSignalId[signalId])
                    continue;
                mapSignalId[link.signal.id] = searchIndices(signalId, spectra);
            }
        }
    }
    const result = [];
    for (let solution of solutions.elements) {
        const spectraResult = JSON.parse(JSON.stringify(spectra));
        const { assignment, score } = solution;
        const atomTypes = Object.keys(assignment);
        for (const atomType of atomTypes) {
            const targetByAtomType = targets[atomType];
            const assignmentPeerAtomType = assignment[atomType];
            for (let index = 0; index < assignmentPeerAtomType.length; index++) {
                const targetID = assignmentPeerAtomType[index];
                if (targetID === '*' || !targetID)
                    continue;
                const target = targetByAtomType[targetID];
                const diaId = diaIDPeerPossibleAssignment[atomType][index];
                for (let link of target.link) {
                    const { spectrumIndex, elementIndex, signalIndex } = mapSignalId[link.signal.id];
                    const spectrum = spectraResult[spectrumIndex];
                    if (isSpectraData1D(spectrum)) {
                        let { ranges } = spectrum;
                        let range = ranges[elementIndex];
                        let signal = range.signals[signalIndex];
                        if (!signal.diaIDs)
                            signal.diaIDs = [];
                        if (signal.diaIDs.includes(diaId))
                            continue;
                        signal.diaIDs.push(diaId);
                    }
                    else {
                        const axis = link.axis;
                        const signal = spectrum.zones[elementIndex].signals[signalIndex];
                        if (!signal[axis].diaIDs)
                            signal[axis].diaIDs = [];
                        if ((_a = signal[axis].diaIDs) === null || _a === void 0 ? void 0 : _a.includes(diaId))
                            continue;
                        (_b = signal[axis].diaIDs) === null || _b === void 0 ? void 0 : _b.push(diaId);
                    }
                }
            }
        }
        result.push({
            score,
            assignment: spectraResult,
        });
    }
    return result;
}
function getSourceOfPartials(store, infoByAtomType, currentAtoms) {
    return store.nSolutions > 0
        ? store.solutions.elements.map((e) => {
            let currentAssignment = e.assignment;
            for (const atom of currentAtoms) {
                currentAssignment[atom] = fillPartial(infoByAtomType[atom].nSources);
            }
            return currentAssignment;
        })
        : initializePartials(infoByAtomType, currentAtoms);
}
function initializePartials(infoByAtomType, currentAtoms) {
    const partial = {};
    const atomsType = Object.keys(infoByAtomType);
    for (const atom of atomsType) {
        const value = currentAtoms.includes(atom) ? null : '*';
        partial[atom] = fillPartial(infoByAtomType[atom].nSources, value);
    }
    return [partial];
}
function fillPartial(nSources, value = null) {
    const partial = new Array(nSources);
    for (let i = 0; i < nSources; i++) {
        partial[i] = value;
    }
    return partial;
}
//# sourceMappingURL=buildAssignments.js.map